import { createContext, PropsWithChildren, useContext, useMemo } from 'react';

import { ApiSchemas, useApiV1Ctx } from '@/components/providers/v1-api';

type Employee = ApiSchemas['EmployeeRead'];

interface ContextType {
  isLoading: boolean;
  employees: Employee[];
  employeesById: Map<string, Employee>;
}

const Context = createContext<ContextType | null>(null);

export function EmployeesProvider({ children }: PropsWithChildren) {
  const { api } = useApiV1Ctx();
  const { data: { items: employees } = { items: [] }, isLoading } = api.employees.getAllEmployeesGet.useQuery(
    undefined,
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    }
  );
  const employeesById = useMemo(() => {
    const employeesById = new Map<string, Employee>();
    if (!employees) return employeesById;
    for (const employee of employees) {
      employeesById.set(employee.id, employee);
    }
    return employeesById;
  }, [employees]);

  return <Context value={{ employees, employeesById, isLoading }}>{children}</Context>;
}

export function useEmployeesCtx() {
  const ctx = useContext(Context);
  if (!ctx) {
    throw new Error('useEmployeesCtx must be used within an EmployeesProvider');
  }
  return ctx;
}

export function useActiveEmployees({ sortBy }: { sortBy?: keyof Employee } = {}) {
  const { employees } = useEmployeesCtx();
  return useMemo(() => {
    const activeEmployees = employees.filter((employee) => employee.status === 'ACTIVE');

    if (!sortBy) return activeEmployees;

    return activeEmployees.sort((a, b) => {
      const aValue = a[sortBy];
      const bValue = b[sortBy];

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return aValue.localeCompare(bValue);
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return aValue - bValue;
      }

      return 0;
    });
  }, [employees, sortBy]);
}

export function useEmployeeOptions() {
  const activeEmployees = useActiveEmployees({ sortBy: 'first_name' });
  return useMemo(
    () =>
      activeEmployees.map((employee) => ({
        value: employee.id,
        label: `${employee.first_name} ${employee.last_name}`,
      })),
    [activeEmployees]
  );
}
